import { makeAutoObservable } from "mobx";

import { hasGroupBounds, isActiveMatrixCell } from "@/components/expression/utils/matrix";

import type { DocumentModel } from "@/models//DocumentModel";
import type { CursorModel } from "@/models/CursorModel";
import type { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import type { MatrixNodeModel } from "@/models/nodes/MatrixNodeModel";
import type { CellPosition, MatrixBounds } from "@corca/data";

export type MatrixSelectionDump = {
  matrixId: string;
  anchor: CellPosition;
  head: CellPosition;
  augmentedBarSelection?: [matrixId: string, colIndex: number];
};

export class MatrixSelectionModel {
  doc: DocumentModel;

  node?: MatrixNodeModel;

  anchor?: CellPosition;

  head?: CellPosition;

  augmentedBarSelection: [matrixId: string, colIndex: number] | undefined = undefined;

  constructor(doc: DocumentModel) {
    this.doc = doc;

    makeAutoObservable(this);
  }

  setupFromCursor(cursor: CursorModel, index?: number) {
    if (!isActiveMatrixCell(cursor)) {
      return false;
    }

    const {
      node,
      node: { parent: matrix },
    } = cursor;

    if (hasGroupBounds(matrix.bounds)) {
      return false;
    }

    if (this.hasSelection(matrix) || this.isSelected(node)) {
      return true;
    }

    const shouldStartSelect = typeof index === "number" ? cursor.index === index : true;

    if (shouldStartSelect) {
      const anchor = matrix.getCellPosition(node);

      if (anchor) {
        this.doc.selection.resetSelection();

        this.node = matrix;
        this.anchor = anchor;
        this.head = anchor;

        return true;
      }

      return false;
    }

    return false;
  }

  moveSelection(deltaX: number, deltaY: number, cb: (cell: InlineNodeModel) => void) {
    if (!this.head || !this.node) {
      return false;
    }

    const head = {
      row: this.head.row + deltaY,
      col: this.head.col + deltaX,
    };

    const cell = this.node.getCellNodeByPosition(head);

    if (cell) {
      this.head = head;
      cb(cell);

      return true;
    }

    return false;
  }

  setSelectionRange(from: CellPosition, to: CellPosition) {
    this.anchor = from;
    this.head = to;
  }

  get currentSelection() {
    if (!this.selectedMatrix || !this.bounds || !this.node || !this.selectionRange) {
      return undefined;
    }

    return {
      node: this.node,
      matrix: this.selectedMatrix,
      nodes: this.selectedMatrix.flat(),
      bounds: this.bounds,
      range: this.selectionRange,
    };
  }

  isSelected(cell?: InlineNodeModel) {
    return !!this.currentSelection?.nodes.find((node) => node === cell);
  }

  hasSelection(node: MatrixNodeModel) {
    return this.node === node;
  }

  resetSelection() {
    this.node = undefined;
    this.anchor = undefined;
    this.head = undefined;
    this.augmentedBarSelection = undefined;
  }

  dumpSelection(): MatrixSelectionDump | undefined {
    if (!this.node || !this.anchor || !this.head) {
      return undefined;
    }

    return {
      matrixId: this.node.id,
      anchor: this.anchor,
      head: this.head,
      augmentedBarSelection: this.augmentedBarSelection,
    };
  }

  restoreSelection(dump: MatrixSelectionDump | undefined) {
    if (!dump) {
      this.resetSelection();
      return;
    }

    const matrixNode = this.doc.nodes.get(dump.matrixId);
    if (!matrixNode || matrixNode.type !== "matrix") {
      this.resetSelection();
      return;
    }

    this.node = matrixNode;
    this.anchor = dump.anchor;
    this.head = dump.head;
    this.augmentedBarSelection = dump.augmentedBarSelection;
  }

  isAugmentedBarSelected(matrixId: string, colIndex: number) {
    const [id, index] = this.augmentedBarSelection ?? [];
    return id === matrixId && index === colIndex;
  }

  private get selectedMatrix(): InlineNodeModel[][] | undefined {
    if (!this.anchor || !this.head || !this.node) {
      return undefined;
    }

    const { matrix, bounds } = this.node;

    const [rowStart, rowEnd] = this.getRange(this.anchor.row, this.head.row, bounds.rows);
    const [colStart, colEnd] = this.getRange(this.anchor.col, this.head.col, bounds.cols);

    return matrix.slice(rowStart, rowEnd).map((row) => row.slice(colStart, colEnd));
  }

  private get bounds(): MatrixBounds | undefined {
    if (!this.selectedMatrix) {
      return undefined;
    }

    const rows = this.selectedMatrix.length;
    const cols = rows > 0 ? this.selectedMatrix[0].length : 0;

    return { rows, cols };
  }

  private get selectionRange() {
    const { anchor, head } = this;

    if (!anchor || !head) {
      return undefined;
    }

    const isAnchorBelow = anchor.row > head.row || anchor.col > head.col;

    if (isAnchorBelow) {
      return [head, anchor];
    }

    return [anchor, head];
  }

  private getRange(start: number, end: number, maxBound: number): [number, number] {
    return [
      Math.max(0, Math.min(start, end)),
      Math.max(0, Math.min(maxBound, Math.max(start, end) + 1)),
    ];
  }
}
